/**
 * Shared base cast for ngit-ci CI events (kinds 9841 / 9842 / 39842).
 *
 * Both kinds carry the same common CI context tags:
 *   ["a", "30617:<owner>:<repo-id>"]  — repository coordinate. Multi-maintainer
 *                                       repos are announced under one coordinate
 *                                       per maintainer, so CI events may carry
 *                                       multiple `a` tags — one per coordinate.
 *   ["c", "<commit-id>"]              — commit the workflow ran against
 *   ["w", "<workflow-path>", "<sha256>"] — selected workflow file path
 *   ["o", "<trigger>"]                — push | pull_request | manual | schedule
 *   ["runner", ...] / ["platform", ...] — optional legacy metadata
 *
 * Trigger context:
 *   push:      ["r", "refs/heads/<branch>" | "refs/tags/<tag>"]
 *   PR:        NIP-22-style ["E", <pr-root>] / ["e", <pr-or-update-trigger>]
 */

import { EventCast } from "applesauce-common/casts/cast";
import { getOrComputeCachedValue } from "applesauce-core/helpers";
import { getTagValue } from "applesauce-core/helpers/event";
import type { NostrEvent } from "nostr-tools";

// Cache symbols (per-event caches, safe to share across both kinds)
const RepoCoordSymbol = Symbol.for("ci-repo-coord");
const RepoCoordsSymbol = Symbol.for("ci-repo-coords");
const CommitIdSymbol = Symbol.for("ci-commit-id");
const WorkflowPathSymbol = Symbol.for("ci-workflow-path");
const TriggerSymbol = Symbol.for("ci-trigger");
const RunnerSymbol = Symbol.for("ci-runner");
const PlatformSymbol = Symbol.for("ci-platform");
const BranchRefSymbol = Symbol.for("ci-branch-ref");
const WorkflowRunIdSymbol = Symbol.for("ci-workflow-run-id");
const PRRootIdSymbol = Symbol.for("ci-pr-root-id");
const TriggerEventIdSymbol = Symbol.for("ci-trigger-event-id");
const JobRefsSymbol = Symbol.for("ci-job-refs");
const ManualTriggerRefSymbol = Symbol.for("ci-manual-trigger-ref");
const ServiceRequestRefSymbol = Symbol.for("ci-service-request-ref");

export interface CIJobRef {
  eventId: string;
  relay: string | undefined;
  pubkey: string | undefined;
  jobId: string | undefined;
}

/** NIP-18 quote identifying the maintainer request for a manual CI replay. */
export interface CIManualTriggerRef {
  eventId: string;
  relay: string | undefined;
  pubkey: string | undefined;
}

/** NIP-18 quote identifying the standing request that authorized a run. */
export interface CIServiceRequestRef {
  eventId: string;
  relay: string | undefined;
  pubkey: string | undefined;
}

export abstract class CIContextCast<
  T extends NostrEvent = NostrEvent,
> extends EventCast<T> {
  /** Convenience accessor — the runner / coordinator identity. */
  get pubkey(): string {
    return this.event.pubkey;
  }

  /** First repository coordinate from the `a` tags (30617:<owner>:<repo-id>). */
  get repoCoord(): string | undefined {
    return getOrComputeCachedValue(this.event, RepoCoordSymbol, () =>
      getTagValue(this.event, "a"),
    );
  }

  /**
   * All repository coordinates from `a` tags. Multi-maintainer repos are
   * announced under one coordinate per maintainer, and CI events may tag
   * all of them.
   */
  get repoCoords(): string[] {
    return getOrComputeCachedValue(this.event, RepoCoordsSymbol, () =>
      this.event.tags.filter(([t, v]) => t === "a" && !!v).map(([, v]) => v),
    );
  }

  /** Commit the workflow ran against (`c` tag). */
  get commitId(): string | undefined {
    return getOrComputeCachedValue(this.event, CommitIdSymbol, () =>
      getTagValue(this.event, "c"),
    );
  }

  /** Selected workflow file path (`w` tag). */
  get workflowPath(): string | undefined {
    return getOrComputeCachedValue(this.event, WorkflowPathSymbol, () =>
      getTagValue(this.event, "w"),
    );
  }

  /** Normalized trigger name (`o` tag, legacy fallback `x`). */
  get trigger(): string | undefined {
    return getOrComputeCachedValue(
      this.event,
      TriggerSymbol,
      () => getTagValue(this.event, "o") ?? getTagValue(this.event, "x"),
    );
  }

  /** Optional runner name. */
  get runner(): string | undefined {
    return getOrComputeCachedValue(this.event, RunnerSymbol, () =>
      getTagValue(this.event, "runner"),
    );
  }

  /** Optional CI platform (github-actions | forgejo-actions | gitlab-ci). */
  get platform(): string | undefined {
    return getOrComputeCachedValue(this.event, PlatformSymbol, () =>
      getTagValue(this.event, "platform"),
    );
  }

  /** Push trigger git ref (`r` tag, e.g. refs/heads/main or refs/tags/v1.0). */
  get branchRef(): string | undefined {
    return getOrComputeCachedValue(
      this.event,
      BranchRefSymbol,
      () =>
        this.event.tags.find(
          ([name, value]) => name === "r" && value?.startsWith("refs/"),
        )?.[1],
    );
  }

  /**
   * Stable workflow-run attempt ID shared by Progress and Workflow Result.
   * Progress uses `d`; results use the non-Git-ref `r` value.
   */
  get workflowRunId(): string | undefined {
    return getOrComputeCachedValue(this.event, WorkflowRunIdSymbol, () => {
      if (this.event.kind === 39842) return getTagValue(this.event, "d");
      return this.event.tags.find(
        ([name, value]) =>
          name === "r" && !!value && !value.startsWith("refs/"),
      )?.[1];
    });
  }

  /** Root PR event id (`E` tag) for PR-triggered workflows. */
  get prRootId(): string | undefined {
    return getOrComputeCachedValue(this.event, PRRootIdSymbol, () =>
      getTagValue(this.event, "E"),
    );
  }

  /**
   * The concrete trigger event id (`e` tag) — the PR root or a PR Update
   * (kind:1619) event for PR-triggered workflows.
   */
  get triggerEventId(): string | undefined {
    return getOrComputeCachedValue(this.event, TriggerEventIdSymbol, () =>
      getTagValue(this.event, "e"),
    );
  }

  /** NIP-18 `q` tags that quote completed CI job results. */
  get jobRefs(): CIJobRef[] {
    return getOrComputeCachedValue(this.event, JobRefsSymbol, () =>
      this.event.tags
        .filter(([name, eventId]) => name === "q" && !!eventId)
        .map(([, eventId, relay, pubkey, jobId]) => ({
          eventId,
          relay,
          pubkey,
          jobId,
        })),
    );
  }

  /**
   * The Manual Trigger request that authorized this replay, when present.
   *
   * ngit-ci marks this NIP-18 quote with `manual-trigger`, which distinguishes
   * it from the event's job-result `q` tags.
   */
  get manualTriggerRef(): CIManualTriggerRef | undefined {
    return getOrComputeCachedValue(this.event, ManualTriggerRefSymbol, () => {
      const tag = this.event.tags.find(
        ([name, eventId, , , marker]) =>
          name === "q" && !!eventId && marker === "manual-trigger",
      );
      if (!tag) return undefined;

      const [, eventId, relay, pubkey] = tag;
      return { eventId, relay, pubkey };
    });
  }

  /** Standing Service Request selected at final runner handoff. */
  get serviceRequestRef(): CIServiceRequestRef | undefined {
    return getOrComputeCachedValue(this.event, ServiceRequestRefSymbol, () => {
      const tag = this.event.tags.find(
        ([name, eventId, , , marker]) =>
          name === "q" && !!eventId && marker === "service-request",
      );
      if (!tag) return undefined;

      const [, eventId, relay, pubkey] = tag;
      return { eventId, relay, pubkey };
    });
  }
}
